import { apiClient } from '@/lib/api'

/**
 * The onboarding questionnaire's three endpoints.
 *
 * They live here rather than in `src/lib/api` because that layer belongs to
 * another wave this week; when it grows an `onboardingApi`, this file is the
 * thing to delete and the screen keeps its imports by pointing at it.
 * `authApi.onboard` already exists there but takes a bare record and
 * discards the response, which is exactly the part this screen needs — the
 * answers come back normalised and the server says which keys it did not
 * know.
 *
 * Everything is typed as *reported*, not as hoped: `questions` is the
 * server's catalogue and may contain a `kind` this build cannot draw, so
 * nothing here narrows it. `onboardModel.ts` does the narrowing, once.
 */
export type OnboardingStatus = 'unanswered' | 'started' | 'dismissed' | 'answered'

export interface OnboardingOption {
  id: string
  label: string
}

/** One catalogue entry, with `kind` left wide on purpose (see above). */
export interface OnboardingQuestion {
  id: string
  prompt: string
  kind: string
  options: OnboardingOption[]
}

export interface OnboardingPreferences {
  defaultExportFormat?: string
  defaultDetectionMethod?: string
  suggestUploadLink?: boolean
  unsupportedFormats?: string[]
  volumeBand?: string
}

export interface OnboardingResponse {
  status: OnboardingStatus
  /** Known questions arrive as `string[]`; a key this build does not know
   *  comes back exactly as it was stored, which may be anything. */
  answers: Record<string, unknown>
  schemaVersion: number
  preferences?: OnboardingPreferences
  questions?: unknown
  answeredAt?: string
  dismissedAt?: string
  updatedAt?: string
  unknownAnswers?: string[]
  unknownOptions?: string[]
}

export interface OnboardingSave {
  firstName?: string
  lastName?: string
  answers?: Record<string, string[]>
  /** False saves a draft. The gateway defaults it to **true**, so a partial
   *  save that omitted it would be recorded as "answered". */
  complete?: boolean
}

export const onboardingApi = {
  /** Prefill, resume and the catalogue, in one request. Never 404s. */
  async get(): Promise<OnboardingResponse> {
    const { data } = await apiClient.axios.get<OnboardingResponse>('/v1/auth/onboard')
    return data
  },

  /** Merges into what is stored, shallowly and per question id. */
  async save(payload: OnboardingSave): Promise<OnboardingResponse> {
    const { data } = await apiClient.axios.post<OnboardingResponse>('/v1/auth/onboard', payload)
    return data
  },

  /** "Später beantworten" — a state of its own, not an empty answer. */
  async dismiss(): Promise<OnboardingResponse> {
    const { data } = await apiClient.axios.post<OnboardingResponse>('/v1/auth/onboard/dismiss')
    return data
  },
}
